(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-sheet-proof]';
  const STATE_SELECTOR = '[data-sheet-proof-state]';
  const BOOK_TITLE_SELECTOR = '[data-sheet-proof-book-title]';
  const ORDER_LINE_SELECTOR = '[data-sheet-proof-order-line]';
  const RETRY_BUTTON_SELECTOR = '[data-sheet-proof-retry-button]';

  const SHEET_TRIGGER_SELECTOR = '[data-sheet-proof-sheet-trigger]';
  const SHARE_BUTTON_SELECTOR = '[data-sheet-proof-share]';

  const CHIPS_SELECTOR = '[data-sheet-proof-chips]';
  const NOTE_TEXTAREA_SELECTOR = '[data-sheet-proof-note-textarea]';
  const NOTES_EMPTY_SELECTOR = '[data-sheet-proof-notes-empty]';

  // 2026-10-01 ("the proof side column matches the design"): see interior-proof.js's identical
  // comment on its own copy of these two selectors for the full reasoning — same split, same
  // page-pair convention, this page's sibling.
  const NOTES_TOGGLE_SELECTOR = '[data-sheet-proof-notes-toggle]';
  const NOTES_PANEL_SELECTOR = '[data-sheet-proof-notes-panel]';

  const REGEN_LINE_SELECTOR = '[data-sheet-proof-regen-line]';
  const REQUEST_CHANGES_SELECTOR = '[data-sheet-proof-request-changes]';
  const SEND_NOTES_SELECTOR = '[data-sheet-proof-send-notes]';

  const LIGHTBOX_SELECTOR = '[data-sheet-proof-lightbox]';
  const LIGHTBOX_INNER_SELECTOR = '[data-sheet-proof-lightbox-inner]';
  const LIGHTBOX_CLOSE_SELECTOR = '[data-sheet-proof-lightbox-close]';

  const APPROVE_DIALOG_SELECTOR = '[data-sheet-proof-approve-dialog]';
  const APPROVE_TRIGGER_SELECTOR = '[data-sheet-proof-approve-trigger]';
  const APPROVE_CANCEL_SELECTOR = '[data-sheet-proof-approve-cancel]';
  const APPROVE_CONFIRM_SELECTOR = '[data-sheet-proof-approve-confirm]';
  const APPROVE_ERROR_SELECTOR = '[data-sheet-proof-approve-error]';

  const REJECT_DIALOG_SELECTOR = '[data-sheet-proof-reject-dialog]';
  const REJECT_CANCEL_SELECTOR = '[data-sheet-proof-reject-cancel]';
  const REJECT_FORM_SELECTOR = '[data-sheet-proof-reject-form]';
  const REJECT_COMMENT_SELECTOR = '[data-sheet-proof-reject-comment]';
  const REJECT_COUNTER_SELECTOR = '[data-sheet-proof-reject-counter]';
  const REJECT_SEND_SELECTOR = '[data-sheet-proof-reject-send]';
  const REJECT_ERROR_SELECTOR = '[data-sheet-proof-reject-error]';

  const CONFIRMATION_DIALOG_SELECTOR = '[data-sheet-proof-confirmation-dialog]';
  const CONFIRMATION_TITLE_SELECTOR = '[data-sheet-proof-confirmation-title]';
  const CONFIRMATION_TEXT_SELECTOR = '[data-sheet-proof-confirmation-text]';
  const CONFIRMATION_DONE_SELECTOR = '[data-sheet-proof-confirmation-done]';

  const LOADING_MESSAGE_SELECTOR = '[data-sheet-proof-loading-message]';

  // ADDENDUM, 2026-09-28: the two outcomes this page's actions can end in, with the exact title
  // text the addendum names for this page's approve ("Approved — we're drawing your book") and
  // for either page's reject ("Got it — we're redrawing").
  const APPROVE_CONFIRMATION_TITLE = "Approved — we're drawing your book";
  const APPROVE_CONFIRMATION_TEXT =
    "We're drawing the rest of your book now, and we'll email you the moment it's ready to review.";
  const REJECT_CONFIRMATION_TITLE = "Got it — we're redrawing";
  const REJECT_CONFIRMATION_TEXT =
    "We're redrawing your character with your notes, and we'll email you once it's ready to review again.";

  // Fixed by the API contract this task builds against — the service side is a sibling task
  // (feat/the-customer-approves-the-character-sheet-on-our-own-page). Do not change this without
  // changing the contract.
  const API_PREFIX = '/apps/personalization/sheet-proof/';

  // Task TL3. Same App Proxy host path and the same `?t=` token as every other GET on this page
  // — the service signs the proxy path, this code does not sign anything. Contract: task S73,
  // which returns a 1080x1080 JPEG, or 404 when the personalization has no cover proof yet.
  const SHARE_API_PREFIX = '/apps/personalization/share-card/';
  const SHARE_CARD_FILE_NAME = 'everlasting-storybooks.jpg';

  // Containers that hold preview artwork. Listeners go on the container rather than on each
  // image so they still fire for layers that are created and destroyed at runtime.
  const PROTECTED_REGION_SELECTORS = [
    '.storefront-proof__sheet',
    '.storefront-proof__lightbox-inner'
  ];

  // assets/proof-protection.js, loaded immediately before this file by the same section. Both
  // are deferred, so script order is execution order and this is already defined.
  const Protection = window.ProofProtection;

  // assets/bot-trap.js (task TL4), loaded by this page's section before this file.
  const BotTrap = window.BotTrap;

  // Statuses the GET endpoint can report. Each one is also the `data-sheet-proof-state` name of
  // its own container in the section markup, so setState(status) works directly for every one of
  // these without a translation table. Note the names: 'redrawing'/'with_team' here, not
  // 'rejected_redrawing'/'rejected_with_team' as on the spreads page — the two services define
  // their own enums and this page follows this one's contract exactly, as instructed.
  const KNOWN_STATUSES = [
    'awaiting_decision',
    'approved',
    'redrawing',
    'with_team',
    'expired'
  ];

  /**
   * Display-only: a straight apostrophe renders with a visible gap in KoPub Batang (the title
   * font) — "Luna' s". Converts to the typographic right single quotation mark (U+2019) for
   * *display* only; the raw API value (`payload.bookTitle`) is never modified or re-sent
   * anywhere. Checked in a real browser that KoPub Batang Bold actually has this glyph before
   * relying on it — see the run report.
   */
  function toDisplayTitle(text) {
    return typeof text === 'string' ? text.replace(/'/g, '’') : text;
  }

  // Product rule, 2026-09-28: "reject always needs one" — required from this page's first line
  // of code, unlike the spreads page where it was added after the fact.
  const REJECT_COMMENT_MAX_LENGTH = 2000;

  // 2026-09-29 task "the proof pages keep the footer down and load gently": both CHOSEN — see
  // interior-proof.js's identical constants for the reasoning.
  const STILL_PREPARING_DELAY_MS = 6000;
  const REQUEST_TIMEOUT_MS = 60000;

  const LOADING_MESSAGE_INITIAL = 'Preparing your character…';
  const LOADING_MESSAGE_STILL_PREPARING =
    'Still preparing your character sheet. The first visit can take a little longer.';

  // sessionStorage key prefix for the reload-survives-token addendum (2026-09-29, "a reload
  // breaks the page") — namespaced by page so this page's tokens can never collide with
  // interior-proof.js's, even for the same personalizationId.
  const TOKEN_STORAGE_PREFIX = 'storefront-proof:sheet-proof:';

  // The statuses that mean "this proof is decided" for the addendum's "clear the stored token
  // once the proof is decided (approved, or rejected and accepted)" — not `expired`, which is not
  // a decision this customer made. Names this page's own enum (`redrawing`/`with_team`, not
  // `rejected_redrawing`/`rejected_with_team` — see KNOWN_STATUSES's own comment on why).
  const DECIDING_STATUSES = ['approved', 'redrawing', 'with_team'];

  class SheetProofPage {
    constructor(root) {
      this.root = root;
      this.states = {};

      root.querySelectorAll(STATE_SELECTOR).forEach((el) => {
        this.states[el.dataset.sheetProofState] = el;
      });

      this.bookTitleEls = Array.from(root.querySelectorAll(BOOK_TITLE_SELECTOR));
      this.orderLineEl = root.querySelector(ORDER_LINE_SELECTOR);
      this.retryButton = root.querySelector(RETRY_BUTTON_SELECTOR);
      this.loadingMessageEl = root.querySelector(LOADING_MESSAGE_SELECTOR);
      this.loadingMessageTimer = null;

      this.sheetTrigger = root.querySelector(SHEET_TRIGGER_SELECTOR);
      this.shareButton = root.querySelector(SHARE_BUTTON_SELECTOR);

      this.chipsEl = root.querySelector(CHIPS_SELECTOR);
      this.noteTextarea = root.querySelector(NOTE_TEXTAREA_SELECTOR);
      this.notesEmptyEl = root.querySelector(NOTES_EMPTY_SELECTOR);

      // The collapsible. `notesOpen` is the one source of truth setNotesOpen() writes to both
      // the toggle's aria-expanded and the panel's hidden attribute from.
      this.notesToggle = root.querySelector(NOTES_TOGGLE_SELECTOR);
      this.notesPanel = root.querySelector(NOTES_PANEL_SELECTOR);
      this.notesOpen = false;

      this.regenLineEl = root.querySelector(REGEN_LINE_SELECTOR);
      // Two different buttons now, where one attribute used to cover both — see
      // NOTES_TOGGLE_SELECTOR's own comment above.
      this.requestChangesButton = root.querySelector(REQUEST_CHANGES_SELECTOR);
      this.sendNotesButton = root.querySelector(SEND_NOTES_SELECTOR);

      // The one sheet from the last GET — kept so the lightbox can use the real width/height
      // instead of re-deriving them from the rendered thumbnail's shrunk CSS size.
      this.sheet = null;

      // A single note, in memory only — there is only one page on this proof, so this is not a
      // Map keyed by page number the way the spreads page needs one.
      this.note = '';

      this.redrawsLeft = null;
      this.redrawsTotal = null;

      this.lightbox = root.querySelector(LIGHTBOX_SELECTOR);
      this.lightboxInner = root.querySelector(LIGHTBOX_INNER_SELECTOR);
      this.lightboxCloseButton = root.querySelector(LIGHTBOX_CLOSE_SELECTOR);
      this.lightboxOpener = null;

      this.approveDialog = root.querySelector(APPROVE_DIALOG_SELECTOR);
      // Two triggers share this attribute — the desktop-only inline button and the mobile
      // sticky-bar button (only one of the two is ever visible at a given width, via CSS).
      this.approveTriggers = Array.from(root.querySelectorAll(APPROVE_TRIGGER_SELECTOR));
      this.approveCancelButton = root.querySelector(APPROVE_CANCEL_SELECTOR);
      this.approveConfirmButton = root.querySelector(APPROVE_CONFIRM_SELECTOR);
      this.approveError = root.querySelector(APPROVE_ERROR_SELECTOR);
      this.approveInFlight = false;

      this.rejectDialog = root.querySelector(REJECT_DIALOG_SELECTOR);
      this.rejectCancelButton = root.querySelector(REJECT_CANCEL_SELECTOR);
      this.rejectForm = root.querySelector(REJECT_FORM_SELECTOR);
      this.rejectComment = root.querySelector(REJECT_COMMENT_SELECTOR);
      this.rejectCounter = root.querySelector(REJECT_COUNTER_SELECTOR);
      this.rejectSendButton = root.querySelector(REJECT_SEND_SELECTOR);
      this.rejectError = root.querySelector(REJECT_ERROR_SELECTOR);
      this.rejectInFlight = false;

      this.confirmationDialog = root.querySelector(CONFIRMATION_DIALOG_SELECTOR);
      this.confirmationTitleEl = root.querySelector(CONFIRMATION_TITLE_SELECTOR);
      this.confirmationTextEl = root.querySelector(CONFIRMATION_TEXT_SELECTOR);
      this.confirmationDoneButton = root.querySelector(CONFIRMATION_DONE_SELECTOR);
      // Set right before showModal(), read by handleConfirmationDialogClose() once the dialog's
      // native `close` event fires — the same event fires whether Done was clicked or Escape was
      // pressed, so one listener covers both (addendum: "Escape closes it").
      this.pendingDecidedStatus = null;

      /**
       * Read once, here, and never again from `window.location` — same reasoning as
       * interior-proof.js: the token is a bearer credential for this one proof, kept only as an
       * instance field, never in a DOM attribute, a cookie, localStorage, or a console.* call.
       *
       * ADDENDUM, 2026-09-29 ("a reload breaks the page") — see interior-proof.js's identical
       * docblock for the full reasoning: `t` from the URL always wins and is stored in
       * sessionStorage, keyed to this exact `p`; only when the URL has no `t` is the stored one
       * read back, and only for this same `p`. sessionStorage is per tab and dies with it.
       * Storage access is wrapped in try/catch (storeToken()/readToken() below); if it throws,
       * this falls back to today's behavior.
       */
      const params = new URLSearchParams(window.location.search);
      this.personalizationId = params.get('p');
      let token = params.get('t');

      if (token) {
        this.storeToken(this.personalizationId, token);
      } else if (this.personalizationId) {
        token = this.readToken(this.personalizationId);
      }

      this.token = token;

      if (params.get('t')) {
        const url = new URL(window.location.href);
        url.searchParams.delete('t');
        window.history.replaceState(null, '', url.pathname + url.search + url.hash);
      }

      this.handleRetryClick = this.handleRetryClick.bind(this);
      this.handleSheetTriggerClick = this.handleSheetTriggerClick.bind(this);
      this.handleChipClick = this.handleChipClick.bind(this);
      this.handleNoteInput = this.handleNoteInput.bind(this);
      this.handleNotesToggleClick = this.handleNotesToggleClick.bind(this);
      this.handleStickyRequestChangesClick = this.handleStickyRequestChangesClick.bind(this);
      this.handleRequestChangesClick = this.handleRequestChangesClick.bind(this);
      this.handleLightboxClick = this.handleLightboxClick.bind(this);
      this.handleApproveTriggerClick = this.handleApproveTriggerClick.bind(this);
      this.handleApproveConfirmClick = this.handleApproveConfirmClick.bind(this);
      this.handleRejectCommentInput = this.handleRejectCommentInput.bind(this);
      this.handleRejectSubmit = this.handleRejectSubmit.bind(this);
      this.handleConfirmationDialogClose = this.handleConfirmationDialogClose.bind(this);

      this.init();
    }

    init() {
      if (this.retryButton) {
        this.retryButton.addEventListener('click', this.handleRetryClick);
      }

      if (this.sheetTrigger) {
        this.sheetTrigger.addEventListener('click', this.handleSheetTriggerClick);
      }

      if (this.chipsEl) {
        this.chipsEl.addEventListener('click', this.handleChipClick);
      }

      if (this.noteTextarea) {
        this.noteTextarea.addEventListener('input', this.handleNoteInput);
      }

      if (this.notesToggle) {
        this.notesToggle.addEventListener('click', this.handleNotesToggleClick);
      }

      if (this.requestChangesButton) {
        this.requestChangesButton.addEventListener('click', this.handleStickyRequestChangesClick);
      }

      if (this.sendNotesButton) {
        this.sendNotesButton.addEventListener('click', this.handleRequestChangesClick);
      }

      if (this.lightboxCloseButton) {
        this.lightboxCloseButton.addEventListener('click', () => this.closeLightbox());
      }

      if (this.lightbox) {
        this.lightbox.addEventListener('click', this.handleLightboxClick);
        this.lightbox.addEventListener('close', () => this.restoreFocusAfterLightbox());
      }

      this.approveTriggers.forEach((button) => {
        button.addEventListener('click', this.handleApproveTriggerClick);
      });

      if (this.approveCancelButton) {
        this.approveCancelButton.addEventListener('click', () => this.approveDialog.close());
      }

      if (this.approveConfirmButton) {
        this.approveConfirmButton.addEventListener('click', this.handleApproveConfirmClick);
      }

      if (this.rejectCancelButton) {
        this.rejectCancelButton.addEventListener('click', () => this.rejectDialog.close());
      }

      if (this.rejectForm) {
        this.rejectForm.addEventListener('submit', this.handleRejectSubmit);
      }

      if (this.rejectComment) {
        this.rejectComment.addEventListener('input', this.handleRejectCommentInput);
      }

      if (this.confirmationDoneButton) {
        this.confirmationDoneButton.addEventListener('click', () => this.confirmationDialog.close());
      }

      if (this.confirmationDialog) {
        this.confirmationDialog.addEventListener('close', this.handleConfirmationDialogClose);
      }

      this.installProtection();
      // setupShare() runs from applyStatusPayload, once the status response carries
      // `shareCardUrl` (S178: the card has its own read-only token; the proof token never
      // leaves this page).

      if (!this.personalizationId || !this.token) {
        this.setState('missing-params');
        return;
      }

      this.checkStatus();
    }

    /**
     * Task TL3. Blocks the one-click ways to keep a preview; see assets/proof-protection.js for
     * what this deliberately does not claim to do.
     */
    installProtection() {
      Protection.protectRegions(this.root, PROTECTED_REGION_SELECTORS);
      Protection.installShortcutGuard(this.root, document);
    }

    /**
     * The share card is the one image the customer is meant to keep. The button starts hidden
     * and is revealed only once the endpoint has actually produced a card, so it is simply
     * absent while task S73 is undeployed and every call 404s.
     */
    setupShare() {
      if (this.share) {
        return;
      }
      if (!this.shareButton || !this.personalizationId || !this.shareCardUrl) {
        return;
      }

      this.share = Protection.createShareController({
        button: this.shareButton,
        url: this.buildShareCardUrl(),
        fileName: SHARE_CARD_FILE_NAME,
        fetch: (input, options) => window.fetch(input, options),
        navigator: window.navigator,
        document: document,
        FileCtor: window.File,
        createObjectURL: (blob) => window.URL.createObjectURL(blob),
        revokeObjectURL: (href) => window.URL.revokeObjectURL(href),
        onError: (reason) => console.warn('[sheet-proof] Share card unavailable', { reason })
      });

      this.shareButton.addEventListener('click', () => this.share.share());
      this.share.probe();
    }

    buildShareCardUrl() {
      return this.shareCardUrl;
    }

    /**
     * ADDENDUM helpers, 2026-09-29 — see interior-proof.js's identical methods for the reasoning.
     * `personalizationId` is part of the key so a token stored for `p=A` is never read back for
     * `p=B`.
     */
    storageKeyFor(personalizationId) {
      return TOKEN_STORAGE_PREFIX + personalizationId;
    }

    storeToken(personalizationId, token) {
      if (!personalizationId || !token) {
        return;
      }

      try {
        window.sessionStorage.setItem(this.storageKeyFor(personalizationId), token);
      } catch (error) {
        // Storage unavailable — fall back to today's behavior.
      }
    }

    readToken(personalizationId) {
      try {
        return window.sessionStorage.getItem(this.storageKeyFor(personalizationId));
      } catch (error) {
        return null;
      }
    }

    clearStoredToken() {
      if (!this.personalizationId) {
        return;
      }

      try {
        window.sessionStorage.removeItem(this.storageKeyFor(this.personalizationId));
      } catch (error) {
        // Nothing to clean up if storage was never reachable in the first place.
      }
    }

    /**
     * 2026-09-29: the loading-state requirements (skeleton, aria-busy, the 6s "still preparing"
     * message) all key off whichever state is showing — see interior-proof.js's identical method.
     */
    setState(name) {
      Object.keys(this.states).forEach((key) => {
        const el = this.states[key];

        if (el) {
          el.hidden = key !== name;
        }
      });

      // TASK TL4: the minimum-time clock starts when there is actually something to decide on,
      // not at page load — the status GET can take seconds before this state appears.
      if (name === 'awaiting_decision') {
        BotTrap.markOpened(this.root, Date.now());
      }

      this.root.setAttribute('aria-busy', name === 'loading' ? 'true' : 'false');

      if (name === 'loading') {
        this.startLoadingMessageTimer();
      } else {
        this.clearLoadingMessageTimer();
      }
    }

    startLoadingMessageTimer() {
      if (!this.loadingMessageEl) {
        return;
      }

      this.loadingMessageEl.textContent = LOADING_MESSAGE_INITIAL;
      window.clearTimeout(this.loadingMessageTimer);

      this.loadingMessageTimer = window.setTimeout(() => {
        this.loadingMessageEl.textContent = LOADING_MESSAGE_STILL_PREPARING;
      }, STILL_PREPARING_DELAY_MS);
    }

    clearLoadingMessageTimer() {
      window.clearTimeout(this.loadingMessageTimer);
      this.loadingMessageTimer = null;
    }

    /**
     * ADDENDUM, 2026-09-28 (owner report, order #41022): switches to a decided state (approved,
     * redrawing, with_team, expired) AND brings it into view — the owner approved this exact
     * page live while scrolled down, and the confirmation text landed off screen above him.
     * Called both right after the confirmation dialog closes and whenever a fresh page load lands
     * directly in one of these states (see applyStatusPayload below).
     */
    revealDecidedState(status) {
      // ADDENDUM, 2026-09-29: "clear the stored token once the proof is decided" — see
      // interior-proof.js's identical comment. Not called for `expired`.
      if (DECIDING_STATUSES.includes(status)) {
        this.clearStoredToken();
      }

      this.setState(status);

      const el = this.states[status];

      if (!el) {
        return;
      }

      const heading = el.querySelector('.storefront-proof__title');

      if (heading) {
        if (!heading.hasAttribute('tabindex')) {
          heading.setAttribute('tabindex', '-1');
        }

        heading.focus({ preventScroll: true });
      }

      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /**
     * ADDENDUM point 1: opens only from a successful approve/reject response — never called from
     * a 4xx/5xx/409 branch, which keep calling checkStatus()/showApproveError()/showRejectError()
     * exactly as before. `status` is applied once the dialog closes (Done or Escape — see
     * handleConfirmationDialogClose), not immediately.
     */
    showConfirmationDialog(title, text, status) {
      this.pendingDecidedStatus = status;

      if (!this.confirmationDialog) {
        this.revealDecidedState(status);
        return;
      }

      this.confirmationTitleEl.textContent = title;
      this.confirmationTextEl.textContent = text;
      this.confirmationDialog.showModal();
    }

    /**
     * Native `<dialog>`'s `close` event fires the same way for the Done button and for Escape, so
     * this one listener satisfies "Escape closes it" for free.
     */
    handleConfirmationDialogClose() {
      const status = this.pendingDecidedStatus;
      this.pendingDecidedStatus = null;

      if (status) {
        this.revealDecidedState(status);
      }
    }

    setBookTitle(bookTitle) {
      const text = toDisplayTitle(bookTitle || 'Your storybook');

      this.bookTitleEls.forEach((el) => {
        el.textContent = text;
      });
    }

    /**
     * Contract fix, 2026-09-29 (service commit 4dd2944): `payload.orderName` is the bare
     * Shopify order name, e.g. orderName: '#41022' — not a pre-formatted string. This builds
     * the display line ("Order #41022 · Proof review") client-side. Hidden for any non-string
     * or empty value, which is also the correct behavior when the field isn't populated yet.
     */
    setOrderName(orderName) {
      if (!this.orderLineEl) {
        return;
      }

      if (typeof orderName !== 'string' || orderName === '') {
        this.orderLineEl.hidden = true;
        this.orderLineEl.textContent = '';
        return;
      }

      this.orderLineEl.textContent = `Order ${orderName} · Proof review`;
      this.orderLineEl.hidden = false;
    }

    buildStatusUrl() {
      const path = API_PREFIX + encodeURIComponent(this.personalizationId);
      return path + '?t=' + encodeURIComponent(this.token);
    }

    /**
     * TASK TL4 appends the two bot signals here. They are QUERY parameters because the service's
     * global ValidationPipe runs `forbidNonWhitelisted: true` and would 400 on unknown BODY
     * fields, and the elapsed time is named `bt`, not `t`, because `t` on these routes is
     * already the auth token (`@Query('t') token`). Both measured — see the run report.
     */
    buildActionUrl(action) {
      const path = API_PREFIX + encodeURIComponent(this.personalizationId) + '/' + action;
      const url = path + '?t=' + encodeURIComponent(this.token);
      return BotTrap.appendSignals(url, this.botTrapVerdict || { hp: '', elapsed: 0 });
    }

    /**
     * 2026-09-29: "the loading state always ends" — see interior-proof.js's identical docblock.
     */
    async checkStatus() {
      this.setState('loading');

      let response;
      const timeoutController = new AbortController();
      const timeoutTimer = window.setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS);

      try {
        response = await fetch(this.buildStatusUrl(), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: timeoutController.signal
        });
      } catch (error) {
        console.warn('[sheet-proof] Status check failed to send', { message: error?.message || null });
        this.setState('network-error');
        return;
      } finally {
        window.clearTimeout(timeoutTimer);
      }

      if (!response.ok) {
        this.handleStatusError(response.status);
        return;
      }

      let payload;

      try {
        payload = await response.json();
      } catch (error) {
        console.warn('[sheet-proof] Status response was not valid JSON');
        this.setState('network-error');
        return;
      }

      this.applyStatusPayload(payload);
    }

    /**
     * 401/404/410 all mean the same thing to the customer: nothing this page can do fixes it.
     * The contract also names an `expired` value inside a 200 body — both routes land on the
     * same "expired" container, exactly as on the spreads page.
     */
    handleStatusError(status) {
      console.warn('[sheet-proof] Status check rejected', { status });

      // ADDENDUM guard, 2026-09-29: "when the service answers 401/403 for it" — see
      // interior-proof.js's identical comment.
      if (status === 401 || status === 403) {
        this.clearStoredToken();
      }

      if (status === 401 || status === 404 || status === 410) {
        this.revealDecidedState('expired');
        return;
      }

      this.setState('network-error');
    }

    applyStatusPayload(payload) {
      const status = payload && payload.status;

      if (!KNOWN_STATUSES.includes(status)) {
        console.warn('[sheet-proof] Status response had an unrecognized status');
        this.setState('network-error');
        return;
      }

      if (typeof payload.shareCardUrl === 'string') {
        this.shareCardUrl = payload.shareCardUrl;
        this.setupShare();
      }

      if (status === 'awaiting_decision') {
        this.setBookTitle(payload.bookTitle);
        this.setOrderName(payload.orderName);
        this.renderSheet(payload.sheet);

        this.note = '';
        if (this.noteTextarea) {
          this.noteTextarea.value = '';
        }

        this.setNotesOpen(false);

        this.redrawsLeft = typeof payload.redrawsLeft === 'number' ? payload.redrawsLeft : null;
        this.redrawsTotal = typeof payload.redrawsTotal === 'number' ? payload.redrawsTotal : null;

        this.updateNotesEmptyHint();
        this.updateRedrawsLine();
        this.updateRequestChangesButtons();
        this.setState('awaiting_decision');
        return;
      }

      if (status === 'approved' || status === 'redrawing' || status === 'with_team') {
        this.setBookTitle(payload.bookTitle);
      }

      // ADDENDUM point 2: "whenever the page loads in a decided state" — a fresh GET landing
      // directly on approved/redrawing/with_team/expired gets the same reveal-and-scroll
      // treatment as closing the confirmation dialog does.
      this.revealDecidedState(status);
    }

    /**
     * Builds the sheet surface here, from the API response, rather than leaving a static element
     * in the Liquid markup — there is no real width/height until this response arrives.
     *
     * TASK TL3 changed this from an <img> to a background-painted div. The <img> was the one
     * thing on this page that made "Save image as" and drag-to-desktop work in a single
     * gesture. The behaviour either side of that change is deliberately identical: the image is
     * still preloaded before anything is shown, `.storefront-proof__thumb-surface` still starts
     * at opacity 0 so the card's own white background shows through until `load` fires, and an
     * `error` still removes the surface rather than leaving a broken frame. The preloading
     * Image is never inserted into the DOM, so the URL's only appearance is the background of
     * the div — see assets/proof-protection.js for what that does and does not achieve.
     *
     * `role="img"` + `aria-label` on the surface carry the alt text the <img> used to hold.
     */
    renderSheet(sheet) {
      this.sheetTrigger.textContent = '';

      if (!sheet || !sheet.imageUrl) {
        this.sheet = null;
        return;
      }

      this.sheet = sheet;
      this.sheetTrigger.style.aspectRatio = sheet.width + ' / ' + sheet.height;

      const surface = document.createElement('div');
      surface.className = 'storefront-proof__thumb-surface';

      const preload = new Image();

      preload.addEventListener('load', () => {
        Protection.setProtectedBackground(surface, sheet.imageUrl, { label: 'Character sheet' });
        surface.classList.add('storefront-proof__thumb-surface--loaded');
      });
      preload.addEventListener('error', () => {
        surface.remove();
      });

      this.sheetTrigger.appendChild(surface);
      this.sheetTrigger.appendChild(Protection.createShield(document));
      preload.src = sheet.imageUrl;
    }

    handleSheetTriggerClick() {
      if (!this.sheet) {
        return;
      }

      this.openLightbox(this.sheetTrigger, this.sheet);
    }

    /**
     * TASK TL3: a background-painted div, not an <img>, for the same reason as renderSheet.
     * The <img> was sized by its width/height attributes; the surface reproduces that fit from
     * the same two numbers (see `.storefront-proof__lightbox-surface`).
     */
    openLightbox(opener, sheet) {
      this.lightboxOpener = opener;
      this.lightboxInner.textContent = '';

      const surface = document.createElement('div');
      surface.className = 'storefront-proof__lightbox-surface';

      Protection.setProtectedBackground(surface, sheet.imageUrl, {
        label: 'Character sheet',
        width: sheet.width,
        height: sheet.height
      });

      this.lightboxInner.appendChild(surface);
      this.lightboxInner.appendChild(Protection.createShield(document));
      this.lightbox.showModal();
    }

    closeLightbox() {
      if (this.lightbox.open) {
        this.lightbox.close();
      }
    }

    handleLightboxClick(event) {
      if (event.target === this.lightbox) {
        this.closeLightbox();
      }
    }

    restoreFocusAfterLightbox() {
      if (this.lightboxOpener) {
        this.lightboxOpener.focus({ preventScroll: true });
      }

      this.lightboxOpener = null;
    }

    /**
     * A chip inserts its text into the note (product brief, "Layout to build"): appended after
     * existing text with "; " as a separator, or set outright if the box is empty.
     */
    handleChipClick(event) {
      const chip = event.target.closest('[data-chip-text]');

      if (!chip || !this.noteTextarea) {
        return;
      }

      const chipText = chip.dataset.chipText;
      const current = this.noteTextarea.value.replace(/\s+$/, '');

      this.noteTextarea.value = current ? current + '; ' + chipText : chipText;
      this.noteTextarea.focus();
      this.handleNoteInput();
    }

    handleNoteInput() {
      this.note = this.noteTextarea.value;
      this.updateNotesEmptyHint();
      this.updateRequestChangesButtons();
      this.maybeAutoOpenNotes();
    }

    updateNotesEmptyHint() {
      if (this.notesEmptyEl) {
        this.notesEmptyEl.hidden = this.note.trim().length > 0;
      }
    }

    /**
     * 2026-10-01 ("the proof side column matches the design"): see interior-proof.js's identical
     * setNotesOpen()/handleNotesToggleClick()/maybeAutoOpenNotes() for the full reasoning — same
     * mechanism, this page's sibling. "Any page already has a note" simplifies to "the one note
     * is non-empty", since this page has only the one.
     */
    setNotesOpen(open) {
      this.notesOpen = open;

      if (this.notesToggle) {
        this.notesToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      if (this.notesPanel) {
        this.notesPanel.hidden = !open;
      }
    }

    handleNotesToggleClick() {
      this.setNotesOpen(!this.notesOpen);
    }

    maybeAutoOpenNotes() {
      if (this.note.trim().length > 0 && !this.notesOpen) {
        this.setNotesOpen(true);
      }
    }

    /**
     * "Request changes in the sticky bar scrolls to the collapsible and opens it; it does not
     * open the reject dialog directly" (brief) — see interior-proof.js's identical method for
     * the full reasoning.
     */
    handleStickyRequestChangesClick() {
      if (this.redrawsLeft === 0 || !this.notesToggle) {
        return;
      }

      this.setNotesOpen(true);
      this.notesToggle.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.notesToggle.focus();
    }

    /**
     * "{left} of {total} redraws left" (brief, verbatim). The old fixed sentence this line used
     * to hold ("We'll redraw your character with your notes.") is not dropped — it is now the
     * static `.storefront-proof__notes-policy` paragraph in the Liquid markup, directly below
     * "Notes for this sheet" — additive to the brief's own structure list, matching
     * interior-proof.js's identical choice (see this task's report). `regenFallbackEl` is gone
     * outright: this one line now does both jobs the two of them used to split.
     */
    updateRedrawsLine() {
      const hasTotal =
        typeof this.redrawsTotal === 'number' && Number.isFinite(this.redrawsTotal) && this.redrawsTotal > 0;
      const left = typeof this.redrawsLeft === 'number' ? this.redrawsLeft : 0;

      if (!this.regenLineEl) {
        return;
      }

      if (hasTotal) {
        this.regenLineEl.textContent = left + ' of ' + this.redrawsTotal + ' redraws left';
      } else {
        const changeWord = left === 1 ? 'change' : 'changes';
        this.regenLineEl.textContent = left + ' ' + changeWord + ' left';
      }
    }

    /**
     * Reconciles two brief statements that only look contradictory: "the button is disabled
     * until at least one note exists" (Regenerations card) and "it opens the confirm step only
     * when at least one note exists; otherwise it scrolls or focuses the notes box" (product
     * rule #1). A truly `disabled` button never dispatches `click`, so it could never scroll or
     * focus anything — so "disabled" here means "styled inactive", via the `--muted` modifier,
     * while staying a real, focusable, clickable button. `redrawsLeft === 0` (product rule #5)
     * is the one genuine, unconditional disable: there is nothing this button can do then.
     *
     * 2026-10-01: styles BOTH buttons identically (the sticky-bar instance and the desktop Send
     * Notes instance) — only their CLICK behaviour differs now.
     */
    updateRequestChangesButtons() {
      const zeroLeft = this.redrawsLeft === 0;
      const hasNote = this.note.trim().length > 0;

      [this.requestChangesButton, this.sendNotesButton].filter(Boolean).forEach((button) => {
        if (zeroLeft) {
          button.textContent = 'Our team will help — reply to this email';
          button.disabled = true;
          button.classList.remove('storefront-proof__button--muted');
          return;
        }

        button.textContent = button === this.sendNotesButton ? 'Send notes' : 'Request changes';
        button.disabled = false;
        button.classList.toggle('storefront-proof__button--muted', !hasNote);
      });
    }

    /**
     * 2026-10-01: now only the desktop Send Notes button's handler — the sticky bar's own click
     * goes to handleStickyRequestChangesClick() instead. Body unchanged below, per the brief
     * ("which runs today's Request changes flow unchanged"), including the scroll-to-textarea
     * behaviour for this page's "reject always needs a note" rule.
     */
    handleRequestChangesClick() {
      if (this.redrawsLeft === 0) {
        return;
      }

      if (!this.note.trim()) {
        if (this.noteTextarea) {
          this.noteTextarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.noteTextarea.focus();
        }
        return;
      }

      this.rejectError.hidden = true;
      this.rejectError.textContent = '';
      this.rejectComment.value = this.note.trim();
      this.updateRejectValidity();
      this.rejectDialog.showModal();
    }

    handleApproveTriggerClick() {
      this.approveError.hidden = true;
      this.approveError.textContent = '';
      this.approveDialog.showModal();
    }

    /**
     * THE DOUBLE-TAP GUARD. `button.disabled = true` runs synchronously, before the `await`, so
     * a second tap that lands before the first response arrives finds a disabled button and
     * never reaches this handler at all. The `if (this.approveInFlight) return` above it is a
     * second, independent check for the same thing — see interior-proof.js's identical comment.
     */
    async handleApproveConfirmClick() {
      if (this.approveInFlight) {
        return;
      }

      // TASK TL4, reworked 2026-10-13. Only the TRAP can stop an approval here. No
      // `enforceTime`: a parent who approves quickly must never have it silently discarded while
      // being shown the confirmation. The elapsed time is measured and sent, never enforced.
      // On a trap trip the customer sees exactly the confirmation a real approval produces.
      const trap = BotTrap.evaluate(this.root, Date.now());

      if (trap.ignore) {
        this.approveDialog.close();
        this.showConfirmationDialog(APPROVE_CONFIRMATION_TITLE, APPROVE_CONFIRMATION_TEXT, 'approved');
        return;
      }

      this.botTrapVerdict = trap;

      this.approveInFlight = true;
      this.approveConfirmButton.disabled = true;
      this.approveError.hidden = true;

      let response;

      try {
        response = await fetch(this.buildActionUrl('approve'), { method: 'POST' });
      } catch (error) {
        console.warn('[sheet-proof] Approve request failed to send', { message: error?.message || null });
        this.showApproveError("We couldn't reach the server. Please try again.");
        return;
      } finally {
        this.approveInFlight = false;
        this.approveConfirmButton.disabled = false;
      }

      if (response.status === 409) {
        this.approveDialog.close();
        this.checkStatus();
        return;
      }

      // ADDENDUM guard, 2026-09-29: "when the service answers 401/403 for it" — cleanup only,
      // does not change which message shows below.
      if (response.status === 401 || response.status === 403) {
        this.clearStoredToken();
      }

      if (!response.ok) {
        console.warn('[sheet-proof] Approve request rejected', { status: response.status });
        this.showApproveError("We couldn't send your approval. Please try again.");
        return;
      }

      let payload;

      try {
        payload = await response.json();
      } catch (error) {
        payload = null;
      }

      this.approveDialog.close();

      if (payload && KNOWN_STATUSES.includes(payload.status)) {
        this.showConfirmationDialog(APPROVE_CONFIRMATION_TITLE, APPROVE_CONFIRMATION_TEXT, payload.status);
      } else {
        this.checkStatus();
      }
    }

    showApproveError(message) {
      this.approveError.textContent = message;
      this.approveError.hidden = false;
    }

    /**
     * Product rule, 2026-09-28: "reject always needs one." Recomputes the counter text and the
     * send button's disabled state on every keystroke; also called when the confirm dialog opens
     * (pre-filled from the note, so normally already valid) and in handleRejectSubmit's finally
     * block.
     */
    updateRejectValidity() {
      const trimmedLength = this.rejectComment.value.trim().length;
      const isValid = trimmedLength >= 1 && trimmedLength <= REJECT_COMMENT_MAX_LENGTH;

      this.rejectCounter.textContent = trimmedLength + ' / ' + REJECT_COMMENT_MAX_LENGTH;
      this.rejectCounter.classList.toggle('storefront-proof__counter--invalid', trimmedLength === 0);
      this.rejectSendButton.disabled = !isValid || this.rejectInFlight;

      return isValid;
    }

    handleRejectCommentInput() {
      this.updateRejectValidity();
    }

    /**
     * Same guard shape as approve. The `!this.updateRejectValidity()` check is defensive, not
     * the primary gate — the primary gate is the send button's own `disabled` state, which a
     * real click cannot bypass.
     */
    async handleRejectSubmit(event) {
      event.preventDefault();

      if (this.rejectInFlight || !this.updateRejectValidity()) {
        return;
      }

      // TASK TL4 — same rule and same reasoning as handleApproveConfirmClick(): trap enforced,
      // time measured and sent but never used to refuse.
      const trap = BotTrap.evaluate(this.root, Date.now());

      if (trap.ignore) {
        this.rejectDialog.close();
        this.showConfirmationDialog(REJECT_CONFIRMATION_TITLE, REJECT_CONFIRMATION_TEXT, 'redrawing');
        return;
      }

      this.botTrapVerdict = trap;

      this.rejectInFlight = true;
      this.rejectSendButton.disabled = true;
      this.rejectError.hidden = true;

      const comment = this.rejectComment.value.trim();
      let response;

      try {
        response = await fetch(this.buildActionUrl('reject'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ comment: comment })
        });
      } catch (error) {
        console.warn('[sheet-proof] Reject request failed to send', { message: error?.message || null });
        this.showRejectError("We couldn't reach the server. Please try again.");
        return;
      } finally {
        this.rejectInFlight = false;
        this.updateRejectValidity();
      }

      if (response.status === 409) {
        this.rejectDialog.close();
        this.checkStatus();
        return;
      }

      // ADDENDUM guard, 2026-09-29: "when the service answers 401/403 for it" — cleanup only,
      // does not change which message shows below.
      if (response.status === 401 || response.status === 403) {
        this.clearStoredToken();
      }

      // 400 means the server-side "reject always needs one" check rejected the comment — the
      // client-side check above makes this the expected shape for an empty/whitespace-only
      // comment that somehow reached the server, not an edge case. The typed text is left
      // exactly as it was: no `.value = ''`, no `.close()`.
      if (response.status === 400) {
        let payload = null;

        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        console.warn('[sheet-proof] Reject request rejected as invalid', { status: 400 });
        this.showRejectError((payload && payload.message) || 'Please tell us what to change.');
        return;
      }

      if (!response.ok) {
        console.warn('[sheet-proof] Reject request rejected', { status: response.status });
        this.showRejectError("We couldn't send this. Please try again.");
        return;
      }

      let payload;

      try {
        payload = await response.json();
      } catch (error) {
        payload = null;
      }

      this.rejectDialog.close();

      if (payload && KNOWN_STATUSES.includes(payload.status)) {
        this.showConfirmationDialog(REJECT_CONFIRMATION_TITLE, REJECT_CONFIRMATION_TEXT, payload.status);
      } else {
        this.checkStatus();
      }
    }

    showRejectError(message) {
      this.rejectError.textContent = message;
      this.rejectError.hidden = false;
    }

    handleRetryClick() {
      this.checkStatus();
    }
  }

  function init() {
    const root = document.querySelector(ROOT_SELECTOR);

    if (!root) {
      return;
    }

    new SheetProofPage(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
